import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Truck, X, Save, History, Check } from 'lucide-react';

const STORAGE_KEY = 'sf_delivery_contacts';

const loadContacts = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
};
const saveContacts = (arr) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr.slice(0, 500))); } catch { /* ignore */ }
};

const onlyDigits = (s) => (s || '').replace(/\D/g, '');

export const DeliveryModal = ({ open, onClose, onConfirm }) => {
  const [contacts, setContacts] = useState(loadContacts());
  const [form, setForm] = useState({ phone: '', name: '', address: '', neighborhood: '', reference: '' });
  const [foundExisting, setFoundExisting] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchHoverIdx, setSearchHoverIdx] = useState(-1);
  const phoneTimer = useRef(null);

  // Busca sugestões pelo telefone OU nome (autocomplete)
  const updateSuggestions = (searchTerm) => {
    const term = (searchTerm || '').toLowerCase().trim();
    const digits = onlyDigits(term);
    if (term.length < 2) {
      setSearchSuggestions([]);
      setSearchHoverIdx(-1);
      return;
    }
    const list = loadContacts();
    const matches = list.filter((c) => {
      const cPhone = onlyDigits(c.phone);
      return (
        (c.name || '').toLowerCase().includes(term) ||
        (digits && cPhone.includes(digits))
      );
    }).slice(0, 6);
    setSearchSuggestions(matches);
    setSearchHoverIdx(matches.length > 0 ? 0 : -1);
  };

  const applyContact = (c) => {
    setForm({
      phone: c.phone || '',
      name: c.name || '',
      address: c.address || '',
      neighborhood: c.neighborhood || '',
      reference: c.reference || '',
    });
    setFoundExisting(true);
    setSearchSuggestions([]);
  };

  useEffect(() => {
    if (open) {
      setContacts(loadContacts());
      setForm({ phone: '', name: '', address: '', neighborhood: '', reference: '' });
      setFoundExisting(false);
      setError('');
      setShowHistory(false);
    }
  }, [open]);

  // Autofill automático: a cada mudança de telefone, debounce 250ms e busca
  useEffect(() => {
    if (phoneTimer.current) clearTimeout(phoneTimer.current);
    const ph = onlyDigits(form.phone);
    if (ph.length < 8) {
      setFoundExisting(false);
      return;
    }
    phoneTimer.current = setTimeout(() => {
      const list = loadContacts();
      const found = list.find((c) => onlyDigits(c.phone) === ph);
      if (found) {
        setForm((prev) => ({
          ...prev,
          name: found.name || '',
          address: found.address || '',
          neighborhood: found.neighborhood || '',
          reference: found.reference || '',
        }));
        setFoundExisting(true);
        setError('');
      } else {
        setFoundExisting(false);
      }
    }, 250);
    return () => phoneTimer.current && clearTimeout(phoneTimer.current);
  }, [form.phone]);

  const handleConfirm = () => {
    if (!form.phone.trim() || !form.name.trim() || !form.address.trim()) {
      setError('Telefone, Nome e Endereço são obrigatórios');
      return;
    }
    const titleCase = (s) => (s || '').toLowerCase().replace(/(^|\s|['-])(\p{L})/gu, (m) => m.toUpperCase());
    const ph = onlyDigits(form.phone);
    const cleanedForm = {
      ...form,
      name: titleCase(form.name),
      address: titleCase(form.address),
      neighborhood: titleCase(form.neighborhood),
    };
    const existing = contacts.find((c) => onlyDigits(c.phone) === ph);

    if (!existing) {
      const newList = [
        { ...cleanedForm, savedAt: new Date().toISOString() },
        ...contacts.filter((c) => onlyDigits(c.phone) !== ph),
      ];
      saveContacts(newList);
      setContacts(newList);
    }

    if (onConfirm) onConfirm(cleanedForm);
    onClose();
  };

  const handleSelectFromHistory = (c) => {
    setForm({
      phone: c.phone, name: c.name, address: c.address,
      neighborhood: c.neighborhood || '', reference: c.reference || ''
    });
    setFoundExisting(true);
    setShowHistory(false);
  };

  const handleRemoveFromHistory = (e, c) => {
    e.stopPropagation();
    const ph = onlyDigits(c.phone);
    const list = contacts.filter((x) => onlyDigits(x.phone) !== ph);
    saveContacts(list);
    setContacts(list);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="delivery-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <Truck className="text-orange-600" size={26} />
            <div>
              <h3 className="text-xl font-bold text-slate-900">Delivery / Entrega</h3>
              <p className="text-xs text-slate-500">Telefone identifica o cliente automaticamente</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="delivery-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

          {/* Telefone — busca automática + sugestões */}
          <div className="relative">
            <Label className="text-xs uppercase tracking-wider">Telefone *</Label>
            <Input
              autoFocus
              value={form.phone}
              onChange={(e) => { setForm({ ...form, phone: e.target.value }); updateSuggestions(e.target.value); }}
              onKeyDown={(e) => {
                if (searchSuggestions.length === 0) return;
                if (e.key === 'ArrowDown') { e.preventDefault(); setSearchHoverIdx(i => Math.min(searchSuggestions.length - 1, i + 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setSearchHoverIdx(i => Math.max(0, i - 1)); }
                else if (e.key === 'Enter' && searchHoverIdx >= 0) { e.preventDefault(); applyContact(searchSuggestions[searchHoverIdx]); }
                else if (e.key === 'Escape') { setSearchSuggestions([]); }
              }}
              placeholder="(00) 00000-0000"
              data-testid="delivery-phone"
              className={foundExisting ? 'border-emerald-500 ring-2 ring-emerald-200' : ''}
            />
            {searchSuggestions.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border-2 border-emerald-300 rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="delivery-suggestions">
                {searchSuggestions.map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    onMouseEnter={() => setSearchHoverIdx(i)}
                    onClick={() => applyContact(c)}
                    className={`w-full text-left px-3 py-2 border-b last:border-b-0 ${i === searchHoverIdx ? 'bg-emerald-100' : 'hover:bg-slate-50'}`}
                    data-testid={`delivery-suggestion-${i}`}
                  >
                    <div className="font-bold text-sm">{c.name} <span className="text-xs text-slate-500 font-normal">· {c.phone}</span></div>
                    <div className="text-[11px] text-slate-600">{c.address}{c.neighborhood ? ` · ${c.neighborhood}` : ''}</div>
                  </button>
                ))}
              </div>
            )}
            {foundExisting ? (
              <p className="text-[11px] text-emerald-700 font-bold mt-1 flex items-center gap-1" data-testid="delivery-found-msg">
                <Check size={12} /> Cliente reconhecido — dados preenchidos automaticamente
              </p>
            ) : onlyDigits(form.phone).length >= 8 ? (
              <p className="text-[11px] text-amber-700 mt-1" data-testid="delivery-new-msg">
                ⓘ Telefone novo — será arquivado ao confirmar a entrega
              </p>
            ) : (
              <p className="text-[10px] text-slate-500 mt-1">Digite telefone OU nome — clientes já atendidos aparecem na lista. ↑↓ navega · Enter seleciona.</p>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Nome do cliente *</Label>
            <Input
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); updateSuggestions(e.target.value); }}
              onKeyDown={(e) => {
                if (searchSuggestions.length === 0) return;
                if (e.key === 'ArrowDown') { e.preventDefault(); setSearchHoverIdx(i => Math.min(searchSuggestions.length - 1, i + 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setSearchHoverIdx(i => Math.max(0, i - 1)); }
                else if (e.key === 'Enter' && searchHoverIdx >= 0) { e.preventDefault(); applyContact(searchSuggestions[searchHoverIdx]); }
                else if (e.key === 'Escape') { setSearchSuggestions([]); }
              }}
              placeholder="Ex.: Maria Silva"
              data-testid="delivery-name"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Endereço de entrega *</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Rua, número"
              data-testid="delivery-address"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Bairro</Label>
            <Input
              value={form.neighborhood}
              onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
              placeholder="Ex.: Vila Planalto"
              data-testid="delivery-neighborhood"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Ponto de referência</Label>
            <Input
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="Ex.: Ao lado da padaria, portão azul"
              data-testid="delivery-reference"
            />
          </div>

          {/* Histórico */}
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowHistory(!showHistory)}
              className="w-full"
              data-testid="delivery-history-toggle"
            >
              <History size={14} className="mr-2" /> {showHistory ? 'Ocultar' : 'Ver'} histórico ({contacts.length})
            </Button>
            {showHistory && (
              <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded divide-y" data-testid="delivery-history">
                {contacts.length === 0 ? (
                  <p className="text-center text-slate-500 p-3 text-sm">Nenhuma entrega registrada ainda</p>
                ) : contacts.map((c, i) => (
                  <div
                    key={i}
                    onClick={() => handleSelectFromHistory(c)}
                    className="flex items-center px-3 py-2 hover:bg-orange-50 text-sm cursor-pointer"
                    data-testid={`delivery-history-${i}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{c.name} <span className="text-xs text-slate-500">· {c.phone}</span></p>
                      <p className="text-xs text-slate-600">{c.address}{c.neighborhood ? ` · ${c.neighborhood}` : ''}</p>
                    </div>
                    <button
                      onClick={(e) => handleRemoveFromHistory(e, c)}
                      className="text-slate-400 hover:text-red-600 ml-2"
                      title="Remover do histórico"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-3 border-t border-slate-200 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
          <Button onClick={handleConfirm} className="flex-1 bg-orange-600 hover:bg-orange-700" data-testid="delivery-confirm">
            <Save size={14} className="mr-1" />
            {foundExisting ? 'Confirmar entrega' : 'Salvar e confirmar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryModal;
